import React, { useState, useEffect, useContext, useRef } from "react";
import Axios from "axios";
import Select from "react-select";
import { API_URL } from "../components/api";
import AuthContext from "../context/AuthContext";

const toDateInput = (v) => {
  if (!v) return "";
  const s = String(v);
  const m = s.match(/^\d{4}-\d{2}-\d{2}/);
  if (m) return m[0];
  try {
    return new Date(s).toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

function EditCable(props) {
  const { user } = useContext(AuthContext);
  const authTokens = JSON.parse(localStorage.getItem("authTokens"));

  const { cable_id, fullname, username: unamePH, pack: packPH, notes: notesPH, expiry: expiryPH } = props;

  const [clientsRaw, setClientsRaw] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [usernameOptions, setUsernameOptions] = useState([]);

  const [client, setClient] = useState(null);
  const [username, setUsername] = useState(null);
  const [pack, setPack] = useState("");
  const [note, setNote] = useState("");
  const [expiry, setExpiry] = useState(toDateInput(expiryPH));

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const isMountedRef = useRef(true);

  const packageOptions = [
    { value: "Basic", label: "Basic" },
    { value: "Popular", label: "Popular" },
    { value: "Premium", label: "Premium" },
  ];

  const customFilter = (option, inputValue) => {
    const words = inputValue.toLowerCase().split(" ").filter(Boolean);
    return words.every((w) => option.label?.toLowerCase().includes(w));
  };

  const getClients = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(`${API_URL}/radius/subscriptions/clients/onboarding/`, {
        headers: {
          Authorization: `Bearer ${authTokens?.access}`,
          "Content-Type": "application/json",
        },
        params: { username: user?.username },
      });

      if (response.status === 200 && isMountedRef.current) {
        const rows = Array.isArray(response.data) ? response.data : [];
        setClientsRaw(rows);
        const uniqueClients = [
          ...new Map(
            rows.map((item) => [
              item.client_id,
              { label: item.fullname, value: item.client_id },
            ])
          ).values(),
        ];
        setClientOptions(uniqueClients);
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to load clients.");
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    getClients();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleClientChange = (opt) => {
    setClient(opt);
    setUsername(null);

    if (!opt) {
      setUsernameOptions([]);
      return;
    }

    const filtered = clientsRaw
      .filter((row) => row.client_id === opt.value)
      .map((row) => ({
        label: row.username,
        value: row.subscription_id,
      }));

    setUsernameOptions(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg(null);

    try {
      setSubmitting(true);

      const payload = {
        Action: "Put",
        cable_subscription_id: cable_id,
        notes: (note || notesPH || "").trim(),
        package: (pack || packPH || "").trim(),
        expiry_date: (expiry || toDateInput(expiryPH) || "").trim(),
        userdealer: user?.username,
      };

      if (client?.value) payload.client_id = client.value;
      if (username?.value) payload.subscription_id = username.value;

      const resp = await Axios.post(`${API_URL}/radius/subscriptions/cable/`, payload, {
        headers: { Authorization: `Bearer ${authTokens?.access}` },
      });

      if (resp.status === 200) window.location.reload(false);
      else setErrorMsg("Update failed. Please verify inputs and try again.");
    } catch (err) {
      console.error(err);
      setErrorMsg("Update failed. Please verify inputs and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container py-3">
      <div >
        <div>
          

          {errorMsg && <div className="alert alert-danger py-2">{errorMsg}</div>}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
             
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Client</label>
                <Select
                  classNamePrefix="react-select"
                  placeholder={loading ? "Loading clients..." : "Choose Client"}
                  options={clientOptions}
                  isLoading={loading}
                  onChange={handleClientChange}
                  filterOption={customFilter}
                  isSearchable
                  value={client}
                  isDisabled={loading || submitting}
                />
                
              </div>

              <div className="col-md-6">
                <label className="form-label">Username (optional)</label>
                <Select
                  classNamePrefix="react-select"
                  placeholder={
                    client
                      ? usernameOptions.length
                        ? "Choose Username"
                        : "No usernames for this client"
                      : "Select a client first"
                  }
                  options={usernameOptions}
                  onChange={setUsername}
                  value={username}
                  isDisabled={!client || !usernameOptions.length || submitting}
                  filterOption={customFilter}
                  isSearchable
                  isClearable
                />
              
              </div>

              <div className="col-md-6">
                <label className="form-label">Package</label>
                <Select
                  classNamePrefix="react-select"
                  placeholder={packPH ? `Current: ${packPH}` : "Choose Package"}
                  options={packageOptions}
                  onChange={(opt) => setPack(opt?.value || "")}
                  value={pack ? { value: pack, label: pack } : null}
                  isClearable
                  isDisabled={submitting}
                />
               
              </div>

              <div className="col-md-6">
                <label className="form-label">Expiry Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={expiry}
                  onChange={(e) => setExpiry(e.target.value)}
                  disabled={submitting}
                />
                
              </div>

              <div className="col-12">
                <label className="form-label">Notes</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={notesPH || "Notes"}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditCable;
